import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2 } from "lucide-react";
import PageHeader from "@/components/site/PageHeader";
import Footer from "@/components/site/Footer";
import { renderInline } from "@/components/site/inlineFormat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "long", day: "numeric",
    });
  } catch {
    return "";
  }
};

export default function Blog() {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    axios.get(`${API}/blog`).then((r) => setPosts(r.data)).catch(() => setPosts([]));
  }, []);

  return (
    <div className="min-h-screen bg-twilight text-cream" data-testid="blog-page">
      <PageHeader />
      <main className="mx-auto max-w-3xl px-6 py-20 md:px-10 md:py-28">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-14 text-center"
        >
          <p className="font-body text-sm uppercase tracking-[0.25em] text-gold">The Blog</p>
          <h1 className="mt-3 font-display text-4xl font-bold text-cream md:text-6xl">Writings & Reflections</h1>
          <p className="mx-auto mt-4 max-w-xl font-body text-lg text-slate-300">
            Faith, family, and the heart behind Hannah Series Official — one story, one prayer, one heart at a time.
          </p>
        </motion.div>

        {posts === null ? (
          <div className="flex justify-center py-20 text-slate-400">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : posts.length === 0 ? (
          <p className="py-20 text-center font-body text-slate-400">No posts yet — check back soon.</p>
        ) : (
          <div className="space-y-6" data-testid="blog-list">
            {posts.map((p, i) => (
              <motion.article
                key={p.slug}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                data-testid={`blog-card-${i}`}
                className="group rounded-2xl border border-white/10 bg-white/[0.04] p-7 transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-gold/40"
              >
                <Link to={`/blog/${p.slug}`} className="block">
                  <time className="font-body text-xs uppercase tracking-[0.2em] text-gold">
                    {fmtDate(p.published_at)}
                  </time>
                  <h2 className="mt-2 font-display text-2xl font-semibold text-cream transition-colors duration-300 group-hover:text-gold md:text-3xl">
                    {renderInline(p.title)}
                  </h2>
                  <p className="mt-3 font-body text-slate-300">{renderInline(p.excerpt)}</p>
                  <span className="mt-4 inline-flex items-center gap-2 font-body text-sm font-medium text-gold">
                    Read more
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </Link>
              </motion.article>
            ))}
          </div>
        )}
      </main>
      <Footer minimal />
    </div>
  );
}
